import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  useMediaQuery,
} from "@mui/material";
import {
  Calender,
  ChevronLeft,
  Destination,
  DownArrow,
  Logout,
  SearchIcon,
  Settings,
  User,
  UserIcon,
  Whatsapp,
} from "@/components/icons";
import FullScreenDialog from "@/components/modals/FullScreenDialog/FullScreenDialog";
import { useTranslations } from "next-intl";
import CustomSearchField from "../Fields/CustomSearchField/CustomSearchField";
import GuestsIcon from "@/components/icons/search/Guests";
import AppButton from "@/components/buttons/AppButton/AppButton";
import { useRouter } from "next/router";
import { formatDateRange, formatDateToDayAndMonth } from "@/helpers/formatDate";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/redux/store";
import { RootState } from "@/redux/types/RootState";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers } from "formik";
import { Room, SearchFormValues } from "@/types/Search";
import { useConfig } from "@/context/ConfigContext";
import * as Yup from "yup";
import { addNotification } from "@/redux/reducer/notificationSlice";
import { initHotelSearch } from "@/utils/search/initHotelSearch";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { loadOptions } from "@/utils/search/hotelLoadOptions";
import AutoSuggestMobile, {
  AutoSuggestMobileRef,
} from "../selects/AutoSuggestMobile/AutoSuggestMobile";
import DateRangePickerMobile from "../Fields/DatePicker/DateRangePickerMobile/DateRangePickerMobile";
import GuestsMobile from "../selects/GuestsMobile/GuestsMobile";
import Link from "next/link";
import AnimatedPlaceholderInput from "../Fields/inputs/AnimatedPlaceholderInput/AnimatedPlaceholderInput";
import { getTotalGuests } from "@/helpers/GetTotalGuests";
import { useAuth } from "@/context/AuthContext";
import SupportDetailCard from "@/components/cards/SupportDetailCard";
import { destroyCookie } from "nookies";
import { useLocalization } from "@/context/LocalizationContext";

interface FormikError {
  [key: string]: string;
}

interface SearchFormMobileProps {
  isSearchPage?: boolean;
  handleLoading?: () => void;
  handleError?: () => void;
  setError?: (err: string) => void;
}

const SearchFormMobile: FC<SearchFormMobileProps> = ({
  isSearchPage,
  handleLoading,
  handleError,
  setError,
}) => {
  const t = useTranslations();
  const trans = useTranslations("guests");
  const router = useRouter();
  const { locale, pathname } = useRouter();
  const { config } = useConfig();
  const { localization, setLocalization } = useLocalization();
  const [loadingButton, setLoadingButton] = useState(false);
  const [openFullScreenDialog, setOpenFullScreenDialog] = useState(false);
  const [openDestinationsDialog, setOpenDestinationsDialog] = useState(false);
  const [openDateRangesDialog, setOpenDateRangesDialog] = useState(false);
  const [openGuestsDialog, setOpenGuestsDialog] = useState(false);
  const [openCurrencyDialog, setOpenCurrencyDialog] = useState(false);
  const { isAuthenticated, logout } = useAuth();

  const [isSupportDrawerOpen, setIsSupportDrawerOpen] = useState(false);

  const today = dayjs().startOf("day");
  const dispatch: AppDispatch = useDispatch();
  const searchState = useSelector((state: RootState) => state.hotelSearch);
  const [nationality, setNationality] = useState(
    searchState.nationality ? searchState.nationality : config.country.code
  );
  const [nights, setNights] = useState(searchState.nights);
  const [startDate, setStartDate] = useState(dayjs(searchState.checkInDate));
  const [endDate, setEndDate] = useState(dayjs(searchState.checkOutDate));
  const [errorStyle, setErrorStyle] = useState(false);
  const currency =
    useSelector((state: RootState) => state.hotelSearch.currency) ||
    config.currency.code;
  const [tempStartDate, setTempStartDate] = useState<dayjs.Dayjs | null>(
    startDate
  );
  const [tempEndDate, setTempEndDate] = useState<dayjs.Dayjs | null>(endDate);

  const navLinks = useMemo(
    () => [
      { label: t("home"), link: "/home" },
      { label: t("bookings"), link: "/bookings" },
      { label: <Whatsapp />, link: "/support" },
    ],
    [t]
  );

  const menuItems = useMemo(
    () => [
      { icon: <User />, label: t("profile"), link: "/profile" },
      { icon: <Settings />, label: t("settings"), link: "/settings" },
    ],
    [t]
  );
  const handleNavigate = async (path: string) => {
    await router.push({ pathname: path }, undefined, { locale });
    setOpen(false);
  };
  const handleLogout = async () => {
    handleClose();
    destroyCookie(null, "next-auth.session-token", { path: "/" });
    logout();
    await router.push("/signin", undefined, { locale });
  };
  const data = {
    logo: "/images/logo.png",
    navLinks: navLinks,
    profileImage: { initials: "BG" },

    language: locale === "en" ? "العربية" : "English",
  };
  const currencies = config.config.locals?.currency.map(
    (currency: {
      currency_code: string;
      currency_name: string;
      currency_name_ar: string;
      symbol: string;
    }) => ({
      label:
        locale === "ar" ? currency.currency_name_ar : currency.currency_name,
      value: currency.currency_code,
      symbol: currency.symbol,
    })
  );
  const [open, setOpen] = useState(false);
    const isMobile = useMediaQuery("(max-width:576px)");

  const [TempCurrency, setTempCurrency] = useState(currency);
  const handleCurrencyChange = (value: string) => {
    setTempCurrency(value);
  };
  const handleClickOpenFullScreenDialog = () => {
    setOpenFullScreenDialog(true);
  };
  const handleClose = () => {
    setOpenFullScreenDialog(false);
  };

  const initialCheckInDate =
    searchState.checkInDate && dayjs(searchState.checkInDate).isBefore(today)
      ? today
      : dayjs(searchState.checkInDate);

  const initialCheckOutDate =
    searchState.checkOutDate && dayjs(searchState.checkInDate).isBefore(today)
      ? initialCheckInDate.add(1, "day")
      : dayjs(searchState.checkOutDate);

  useEffect(() => {
    setStartDate(initialCheckInDate);
    setEndDate(initialCheckOutDate);
  }, []);

  const [rooms, setRooms] = useState<Room[]>(searchState.rooms);
  const [selectedRooms, setSelectedRooms] = useState<Room[]>(searchState.rooms);

  const adultsTotal = selectedRooms.reduce(
    (total, room) => total + room.adults,
    0
  );
  const childrenTotal = selectedRooms.reduce(
    (total, room) => total + room.children.length,
    0
  );

  const handleApply = (updatedRooms: Room[]) => {
    setRooms(updatedRooms);
    setSelectedRooms(updatedRooms);
  };

  const formatSummary = (
    t: (key: string, values?: Record<string, any>) => string
  ) => {
    let summary = `${t("guestRoom", { count: rooms.length })}, `;
    summary += `${t("adult", { count: adultsTotal })}${
      childrenTotal > 0 ? ", " : ""
    }`;
    summary += childrenTotal ? `${t("child", { count: childrenTotal })}` : "";

    return summary.length > 55 ? `${summary.slice(0, 55)}...` : summary;
  };

  const initialValues: SearchFormValues = {
    destination: searchState.destination,
    checkInDate: initialCheckInDate,
    checkOutDate: initialCheckOutDate,
    nights: nights,
    nationality: nationality,
    currency: currency,
  };

  const HotelSchema = Yup.object({
    destination: Yup.object({
      label: Yup.string(),
      value: Yup.string().required(t(`searchForm.destinationRequired`)),
      countryCode: Yup.string(),
      country: Yup.string(),
    })
      .nullable()
      .required(t(`searchForm.destinationRequired`)),
  });

  const handleSubmit = async (
    values: SearchFormValues,
    { setSubmitting }: FormikHelpers<SearchFormValues>
  ) => {
    setSubmitting(true);

    if (!values.destination.value) {
      dispatch(
        addNotification({
          type: "error",
          title: t(`searchForm.destinationRequired`),
          subtitle: t(`searchForm.selectDestination`),
        })
      );
      setErrorStyle(true);
      setSubmitting(false);
      return;
    }

    try {
      handleLoading?.();
      setErrorStyle(false);
      setLoadingButton(true);

      const response = await initHotelSearch(
        {
          preferences: {
            checkin:
              startDate?.format("YYYY-MM-DD") ?? searchState?.checkInDate!,
            nights: nights.toString(),
            nationality: nationality,
            currency: currency,
            nightly_price: false,
          },
          device: isMobile ? "web_mobile":"web",
          rooms: rooms,
          ...(values.destination.isProperty
            ? { hotel_codes: [values.destination.value] }
            : { destination_code: values.destination.value }),
          language: locale === "ar" ? "ar" : "en",
        },
        dispatch
      );

      dispatch(
        setSearchValues({
          checkInDate: startDate?.format("YYYY-MM-DD"),
          checkOutDate: endDate?.format("YYYY-MM-DD"),
          nights: nights,
          currency: currency,
          ratings: [],
          destination: values.destination,
          nationality: nationality,
          rooms: rooms,
        })
      );

      if (response.code === 200 && response.response?.sid) {
        router.push(`/search?sid=${response.response.sid}`, undefined, {
          locale: locale,
        });
      } else if (response.error === "no_hotels_found_in_destination") {
        setError?.("no_hotels_found_in_destination");
        handleError?.();
      } else if (response.code === 2) {
        dispatch(
          addNotification({
            type: "info",
            title: t(`error.Subtitle2`),
            subtitle: response.message,
          })
        );
        setError?.("err");
        handleError?.();
        setLoadingButton(false);
      } else if (response.code === 598) {
        dispatch(
          addNotification({
            type: "error",
            title: "Connection Error",
            subtitle: response.error,
          })
        );
        setLoadingButton(false);
      } else {
        setError?.("err");
        dispatch(
          addNotification({
            type: "error",
            title: t(`error.SomethingWrong`),
            subtitle: t(`error.Subtitle1`),
          })
        );

        handleError?.();
        setLoadingButton(false);
      }
    } catch {
      dispatch(
        addNotification({
          type: "error",
          title: t(`error.SomethingWrong`),
          subtitle: t(`error.Subtitle2`),
        })
      );
      setLoadingButton(false);
    } finally {
      setSubmitting(false);
      setErrorStyle(false);
      if (isSearchPage) {
        setLoadingButton(false);
        setOpenFullScreenDialog(false);
      }
    }
  };

  const autoSuggestRef = useRef<AutoSuggestMobileRef>(null);

  const switchLanguage = (newLocale: string) => {
    router.push({ pathname, query: router.query }, undefined, {
      locale: newLocale,
    });
       setOpen(false);
                setOpenFullScreenDialog(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCurrency = event.target.value;
    setTempCurrency(newCurrency);
  };

  useEffect(() => {
    if (openDestinationsDialog) {
      setTimeout(() => {
        autoSuggestRef.current?.focusAndSelect?.();
      }, 0);
    }
  }, [openDestinationsDialog]);
  const fullString = `${formatDateToDayAndMonth(
    searchState.checkInDate,
    locale
  )} - ${formatDateToDayAndMonth(
    searchState.checkOutDate,
    locale
  )}, ${formatSummary(trans)}`;
  const truncatedString =
    fullString.length > 46 ? fullString.slice(0, 43) + "..." : fullString;

  const fullDateStr = formatDateRange([startDate, endDate], locale ?? "en");
  const truncated =
    fullDateStr.length > 28 ? fullDateStr.slice(0, 28) + "…" : fullDateStr;
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, formikHelpers) => {
        const errors: FormikError = {};
        await HotelSchema.validate(values, { abortEarly: false }).catch(
          (err) => {
            err.inner.forEach((validationError: any) => {
              errors[validationError.path as keyof SearchFormValues] =
                validationError.message;
            });
          }
        );

        if (Object.keys(errors).length === 0) {
          await handleSubmit(values, formikHelpers);
          setErrorStyle(false);
        } else {
          formikHelpers.setErrors(errors);
          Object.keys(errors).forEach((key) => {
            dispatch(
              addNotification({
                type: "error",
                title: errors[key],
                subtitle: t(`error.Subtitle1`),
              })
            );
            setErrorStyle(true);
          });
        }
      }}
    >
      {({ setFieldValue, values, submitForm }) => (
        <Form className="w-full">
          <div className="w-full" data-testid="SearchFormMobile">
            {!isSearchPage ? (
              //             <div className=" flex flex-col gap-4">

              //             <Card className="max-w-[991px] px-4 !w-full border border-[#D8DCDE] !rounded-lg !shadow-none">
              //               <CardContent className="!p-0">
              //                 <div className="relative">
              //                   <div className="absolute top-1/2 ltr:left-1  rtl:right-1 transform -translate-y-1/2">
              //                     <SearchIcon size={16} color="#1E2F56" />
              //                   </div>
              //                   <button
              //                     type="button"
              //                     className="w-full bg-transparent"
              //                     onClick={handleClickOpenFullScreenDialog}
              //                   >
              //                     {}
              //                     {!searchState.destination.label ? (
              //                       <AnimatedPlaceholderInput />
              //                     ) : (
              //                       <div
              //                         className="text-[#1E2F56] text-start flex flex-row  gap-3 items-center   text-xs leading-5 h-[52px] w-full ps-10 border-none caret-transparent focus-visible:ring-0   bg-white  outline-none bg-transparent"
              //                       >
              //                         <div className="flex flex-col items-start gap-[2px] w-full ">
              //                           <span className="font-bold">{
              //   searchState.destination.label
              // }</span>
              // <span>
              //    {truncatedString}
              // </span>
              //                         </div>

              //                         <svg
              //                           className="!w-4 !h-4 rtl:rotate-90 rtl:scale-[-1]"
              //                           width="24"
              //                           height="25"
              //                           viewBox="0 0 24 25"
              //                           fill="none"
              //                           xmlns="http://www.w3.org/2000/svg"
              //                         >
              //                           <path
              //                             d="M16.6953 4.47119C18.8212 3.45948 21.04 5.67924 20.0283 7.8042L14.4766 19.4634C13.5282 21.4548 10.653 21.3313 9.87793 19.2661L8.85059 16.5269V16.5259C8.77539 16.3256 8.65808 16.1439 8.50684 15.9927C8.35559 15.8414 8.17387 15.7241 7.97363 15.6489H7.97266L5.23438 14.6216C3.16863 13.8474 3.04464 10.9713 5.03613 10.0229L16.6953 4.47119Z"
              //                             fill="white"
              //                             stroke="#1E2F56"
              //                             strokeWidth="2"
              //                           />
              //                         </svg>
              //                       </div>
              //                     )}
              //                   </button>
              //                 </div>
              //               </CardContent>
              //             </Card>
              //                    <AppButton
              //                     variant="primary"
              //                     label={t(`searchForm.Search`)}
              //                     className="text-white w-full !h-[40px] !rounded-[40px]"
              //                     textClassName="c-md:!text-xl !text-base  "
              //                     type="submit"
              //                     loading={loadingButton}
              //                     disabled={loadingButton}
              //                     onClick={() => submitForm()}
              //                   />
              //                   </div>
              <Card className=" mt-0 bg-white !rounded-3xl !shadow-none  border-4 border-primary ">
                <CardContent className="!p-4 space-y-4">
                  <CustomSearchField
                    isSearchForm
                    icon={<Destination className="c-md:size-6 size-5" />}
                    isDestination
                    label={t(`searchForm.Destination`)}
                    name="destination"
                    className="relative cursor-pointer min-w-full min-h-6 w-full !border-[#E2EDF8]"
                    onClick={() => setOpenDestinationsDialog(true)}
                    content={
                      <button
                        type="button"
                        className="text-[#191E3B] text-start w-full h-full text-sm leading-5  outline-none bg-transparent"
                      >
                        {searchState.destination.label
                          ? searchState.destination.label
                          : t(`searchForm.destinationPlaceholder`)}
                      </button>
                    }
                  />
                  <CustomSearchField
                    isSearchForm
                    icon={<Calender className=" size-5" color="#1D2E56" />}
                    label={t(`searchForm.Date`)}
                    name="date"
                    className="relative cursor-pointer w-full !border-[#E2EDF8]"
                    onClick={() => {
                      setTempStartDate(startDate);
                      setTempEndDate(endDate);
                      setOpenDateRangesDialog(true);
                    }}
                    content={
                      <button
                        type="button"
                        className="text-[#191E3B] text-sm leading-5  outline-none bg-transparent"
                      >
                        {truncated}
                      </button>
                    }
                  />
                  <CustomSearchField
                    isSearchForm
                    icon={<GuestsIcon className="md:size-6 size-5" />}
                    label={t("guests.label")}
                    name="guests"
                    className="relative cursor-pointer w-full !border-[#E2EDF8]"
                    onClick={() => setOpenGuestsDialog(true)}
                    content={
                      <>
                        <button
                          type="button"
                          className="text-[#191E3B] text-sm leading-5  outline-none bg-transparent"
                        >
                          {formatSummary(trans)}
                        </button>
                      </>
                    }
                  />
                  <AppButton
                    variant="primary"
                    label={t(`searchForm.Search`)}
                    className=" w-full !h-[60px] !rounded-[60px]"
                    textClassName="c-md:!text-xl !text-base   text-primary"
                    type="submit"
                    loading={loadingButton}
                    disabled={loadingButton}
                    onClick={() => submitForm()}
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-start">
                <Link href="/home" passHref locale={locale}>
                  <ChevronLeft
                    className="!w-5 !h-5 mt-1 rtl:scale-[-1]"
                    color="#003d6b"
                  />
                </Link>
                <button
                  type="button"
                  onClick={handleClickOpenFullScreenDialog}
                  className="flex items-center w-full bg-transparent   justify-center   "
                >
                  <div className="flex flex-col items-center  ">
                    <span className="text-[#003D6B] text-xl mb-[9px]">
                      {searchState.destination.label}
                    </span>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center ">
                        <span className="text-[#9CA3AF] text-[14px]">
                          {getTotalGuests(searchState.rooms)}
                        </span>
                        <UserIcon className="w-[20px] h-[14px] object-fill" />
                      </div>
                      <span className="text-[#9CA3AF] text-[14px] ">
                        {`${formatDateToDayAndMonth(
                          searchState.checkInDate,
                          locale
                        )} - ${formatDateToDayAndMonth(
                          searchState.checkOutDate,
                          locale
                        )}`}
                      </span>
                      <DownArrow className="w-[12px] h-[12px] object-fill ms-1" />
                    </div>
                  </div>
                </button>
              </div>
            )}

            <FullScreenDialog
              open={openFullScreenDialog}
              handleClose={handleClose}
              hasBackBtn
              hasMenu
              title={t("Properties search")}
              subTitle={t("searchForm_subtitle")}
              handleOpenMenu={() => setOpen(true)}
              children={
                <Card className="mx-4 mt-0 bg-white !rounded-3xl !shadow-none">
                  <CardContent className="!p-4 space-y-4">
                    <CustomSearchField
                      isSearchForm
                      icon={<Destination className="c-md:size-6 size-5" />}
                      isDestination
                      label={t(`searchForm.Destination`)}
                      name="destination"
                      className="relative cursor-pointer min-w-full min-h-6 w-full !border-[#E2EDF8]"
                      onClick={() => setOpenDestinationsDialog(true)}
                      content={
                        <button
                          type="button"
                          className="text-[#191E3B] text-start w-full h-full text-sm leading-5  outline-none bg-transparent"
                        >
                          {searchState.destination.label
                            ? searchState.destination.label
                            : t(`searchForm.destinationPlaceholder`)}
                        </button>
                      }
                    />
                    <CustomSearchField
                      isSearchForm
                      icon={<Calender className=" size-5" />}
                      label={t(`searchForm.Date`)}
                      name="date"
                      className="relative cursor-pointer w-full !border-[#E2EDF8]"
                      onClick={() => {
                        setTempStartDate(startDate);
                        setTempEndDate(endDate);
                        setOpenDateRangesDialog(true);
                      }}
                      content={
                        <button
                          type="button"
                          className="text-[#191E3B] text-sm leading-5  outline-none bg-transparent"
                        >
                          {formatDateRange(
                            [startDate, endDate],
                            locale ?? "en"
                          )}
                        </button>
                      }
                    />
                    <CustomSearchField
                      isSearchForm
                      icon={<GuestsIcon className="md:size-6 size-5" />}
                      label={t("guests.label")}
                      name="guests"
                      className="relative cursor-pointer w-full !border-[#E2EDF8]"
                      onClick={() => setOpenGuestsDialog(true)}
                      content={
                        <>
                          <button
                            type="button"
                            className="text-[#191E3B] text-sm leading-5  outline-none bg-transparent"
                          >
                            {formatSummary(trans)}
                          </button>
                        </>
                      }
                    />
                    <AppButton
                      variant="primary"
                      label={t(`searchForm.Search`)}
                      className=" w-full !h-[60px] !rounded-[60px]"
                      textClassName="c-md:!text-xl !text-base   text-primary"
                      type="submit"
                      loading={loadingButton}
                      disabled={loadingButton}
                      onClick={() => submitForm()}
                    />
                  </CardContent>
                </Card>
              }
            />

            <FullScreenDialog
              open={open}
              hasBorder
              handleClose={() => setOpen(false)}
              children={
                <div className="flex flex-col items-start   bg-[#F7FAFD] min-w-[360px] !h-full !min-h-fit">
                  <div className="flex flex-col items-start gap-1 px-4  w-full ">
                    {!isAuthenticated && (
                      <>
                        <button className="rounded-[24px] text-[#003d6b] w-full border-[1px] border-[#003d6b] flex px-6 py-2 justify-center items-center gap-2   text-sm font-medium leading-6">
                          {t("login")}
                        </button>
                        <Divider className="!border-[#E7F0F9] border-2  !mt-3  font-normal w-full" />
                      </>
                    )}

                    {data.navLinks.map((link) => {
                      const isActive = router.pathname === link.link;
                      const handleClick = () => {
                        if (isActive) {
                          setOpen(false);
                        }
                      };
                      return (
                        <div
                          key={`${link.link}-sm`}
                          className={`!w-full flex flex-col items-start gap-2`}
                        >
                          {link.link === "/support" ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsSupportDrawerOpen(true);
                              }}
                              className={`text-[#1E293B] text-sm leading-6 font-medium h-10 flex items-center justify-between w-full   
                                   `}
                            >
                              {t("support")}
                              <Whatsapp />
                            </button>
                          ) : (
                            <Link
                              href={{
                                pathname: link.link,
                              }}
                              locale={locale}
                              onClick={handleClick}
                              className={`text-[#1E293B] text-sm leading-6 font-medium h-10 flex items-center justify-between w-full   
                                   `}
                            >
                              <>
                                {link.label}
                                <ChevronLeft
                                  className="w-[16px] h-[16px] ltr:scale-[-1]"
                                  color="#003d6b"
                                />
                              </>
                            </Link>
                          )}

                          <Divider className="!border-[#E7F0F9] border-2  my-2  font-normal w-full" />
                        </div>
                      );
                    })}

                    <button
                      onClick={() => setOpenCurrencyDialog(true)}
                      className="text-[#1E293B] text-sm leading-6 w-full font-medium mt-2 flex items-center justify-between"
                    >
                      <span>{t("currency")}</span>

                      <span>{currency}</span>
                    </button>

                    <Divider className="!border-[#E7F0F9] border-2  !my-3  font-normal w-full" />

                    <button
                      onClick={() =>
                        switchLanguage(locale === "en" ? "ar" : "en")
                      }
                      className="text-[#1E293B] text-[16px] font-medium leading-normal me-6 w-full flex items-center justify-between"
                    >
                      <span className="font-medium text-sm text-slate-800 ">
                        {t("language")}
                      </span>
                      <span className="ms-2">
                        {locale === "en" ? "العربية" : "English"}
                      </span>
                    </button>
                    {isAuthenticated && (
                      <>
                        <div className="mt-6 mb-3 font-bold text-lg ">
                          {t(`My account`)}{" "}
                        </div>

                        <div className="flex flex-col items-start justify-center w-full ">
                          {menuItems.map((item) => (
                            <>
                              <button
                                key={item.link}
                                onClick={() => {
                                  handleNavigate(item.link);
                                }}
                                className={`text-[#1E293B] text-sm   !border-[#E7F0F9] border-b-[1px] leading-6 font-medium h-[52px] flex items-center justify-between w-full `}
                              >
                                <span className="font-medium text-sm text-slate-800 ">
                                  {item.label}
                                </span>
                                <ChevronLeft
                                  className="w-[16px] h-[16px] ltr:scale-[-1]"
                                  color="#003d6b"
                                />
                              </button>
                            </>
                          ))}
                        </div>

                        <button
                          onClick={handleLogout}
                          className="flex justify-between items-center w-full gap-2  py-2 hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-sm text-slate-800  ">
                            {t("logout")}
                          </span>
                          <Logout />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              }
            />
            <FullScreenDialog
              open={openDestinationsDialog}
              handleClose={() => setOpenDestinationsDialog(false)}
              title={t("Property search")}
              hasClearBtn={values.destination ? true : false}
              handleClear={() => {
                setFieldValue("destination", { label: "", value: "" });
                autoSuggestRef.current?.clearInput();
              }}
              children={
                <AutoSuggestMobile
                  ref={autoSuggestRef}
                  name="destination"
                  placeholder={t(`searchForm.destinationPlaceholder`)}
                  errorStyle={errorStyle}
                  value={values.destination}
                  loadOptions={loadOptions}
                  onOptionSelect={(option) => {
                    setFieldValue("destination", option);
                    setErrorStyle(false);
                    if (option !== null) {
                      setOpenDestinationsDialog(false);
                    }
                    if (option === null) {
                      setFieldValue("destination", {
                        label: "",
                        value: "",
                      });
                    }
                  }}
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    e.currentTarget.select();
                    setErrorStyle(false);
                  }}
                />
              }
            />

            <FullScreenDialog
              open={openDateRangesDialog}
              handleClose={() => setOpenDateRangesDialog(false)}
              title={t("selectDate")}
              hasApplyBtn
              handleApply={() => {
                setOpenDateRangesDialog(false);
                if (tempStartDate && tempEndDate) {
                  setStartDate(tempStartDate);
                  setEndDate(tempEndDate);
                  const numberOfNights = tempEndDate.diff(tempStartDate, "day");
                  setNights(numberOfNights);
                }
              }}
              children={
                <>
                  <DateRangePickerMobile
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    onChange={(newStartDate, newEndDate) => {
                      if (newStartDate && newEndDate) {
                        const maxNights = config.config.limits.room_nights_max;
                        const nights = newEndDate.diff(newStartDate, "day");
                        if (nights > maxNights) {
                          setTempEndDate(newStartDate.add(maxNights, "day"));
                        } else {
                          setTempEndDate(newEndDate);
                        }
                        setTempStartDate(newStartDate);
                      }
                    }}
                    dropdownIsOpen={openDateRangesDialog}
                  />
                </>
              }
            />

            <FullScreenDialog
              open={openGuestsDialog}
              handleClose={() => {
                setOpenGuestsDialog(false);
                setSelectedRooms(rooms);
              }}
              title={t("AddGuest")}
              hasApplyBtn
              handleApply={() => {
                setOpenGuestsDialog(false);
                handleApply(selectedRooms);
              }}
              children={
                <>
                  <GuestsMobile
                    onApply={setSelectedRooms}
                    rooms={selectedRooms}
                    onClose={() => setOpenGuestsDialog(false)}
                  />
                </>
              }
            />

            <FullScreenDialog
              open={openCurrencyDialog}
              handleClose={() => setOpenCurrencyDialog(false)}
              title={t("Currencies")}
              hasApplyBtn
              handleApply={() => {
                setOpenCurrencyDialog(false);
                setOpen(false);
                setOpenFullScreenDialog(false);
                dispatch(setSearchValues({ currency: TempCurrency }));
                setLocalization({
                  currency: TempCurrency,
                });
              }}
              hasBorder
              children={
                <RadioGroup
                  value={TempCurrency}
                  onChange={handleChange}
                  name="currency"
                  className={`w-full px-4`}
                >
                  {currencies.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={
                        <Radio
                          sx={{
                            color: "#bc9a20",
                            "& .MuiSvgIcon-root": {
                              fontSize: 24,
                              color: "#bc9a20",
                            },
                          }}
                        />
                      }
                      label={
                        currency ? (
                          <div className="flex items-center ">
                            <span className="ms-2 font-semibold">
                              {option.symbol}
                            </span>
                            <span className="ms-2">{option.label}</span>
                          </div>
                        ) : (
                          option.label
                        )
                      }
                      className={`gap-2 flex items-center`}
                    />
                  ))}
                </RadioGroup>
              }
            />

            <FullScreenDialog
              open={isSupportDrawerOpen}
              handleClose={() => setIsSupportDrawerOpen(false)}
              title={t(`supportObj.how_can_we_help`)}
              hasBorder
              children={
                <div className="flex flex-col items-start gap-4  py-6 bg-[#F7FAFD] min-w-[360px] !h-full !min-h-fit">
                  <SupportDetailCard />
                </div>
              }
            ></FullScreenDialog>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SearchFormMobile;
